import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AsociacionEstadoService, ETIQUETA_ESTADO, EstadoAsociacion, estadoDe } from '../../../services/asociacion-estado.service';
import { PermisosService } from '../../../services/permisos.service';
import { AlertService } from '../../alert/alert.service';
import { AlertButtonType } from '../../dialog-alert/AlertButtonType.enum';
import { DialogAlertService } from '../../dialog-alert/dialog-alert.service';

/**
 * Sección «Estado y acceso» de «Editar asociación». Es INDEPENDIENTE del formulario de datos: tiene su propio botón, de modo
 * que guardar otros campos nunca cambia el estado ni al revés. Solo se puede editar con el permiso global
 * `asociaciones:estado` (la API lo vuelve a exigir). Desactivar exige marcar la casilla de confirmación y aceptar un diálogo.
 */
@Component({
  selector: 'app-estado-acceso-editor',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './estado-acceso-editor.component.html'
})
export class EstadoAccesoEditorComponent implements OnChanges {
  @Input({ required: true }) asociacion!: { id?: number | null; nombre?: string | null; active?: number | boolean | null };
  /** Valor de `active` que ha quedado guardado tras un cambio correcto. */
  @Output() estadoCambiado = new EventEmitter<number | null>();

  seleccion: '' | 'activa' | 'desactivada' = '';
  confirmoBloqueo = false;
  guardando = false;

  constructor(
    private readonly permisos: PermisosService,
    private readonly dialogo: DialogAlertService,
    private readonly alertas: AlertService,
    private readonly estadoService: AsociacionEstadoService
  ) {}

  ngOnChanges(): void {
    const actual = this.estadoActual;
    this.seleccion = actual === 'sin-definir' ? '' : actual;
    this.confirmoBloqueo = false;
  }

  get estadoActual(): EstadoAsociacion { return estadoDe(this.asociacion?.active); }
  get etiquetaActual(): string { return ETIQUETA_ESTADO[this.estadoActual]; }
  get puedeCambiar(): boolean { return this.permisos.tieneGlobal('asociaciones:estado'); }
  get bloqueoActivo(): boolean { return Boolean(this.permisos.contexto()?.bloqueoAsociacionesActivo); }
  get hayCambio(): boolean { return this.seleccion !== '' && this.seleccion !== this.estadoActual; }
  get pideConfirmacionExtra(): boolean { return this.seleccion === 'desactivada' && this.hayCambio; }
  get puedeAplicar(): boolean {
    return this.puedeCambiar && this.hayCambio && !this.guardando && (!this.pideConfirmacionExtra || this.confirmoBloqueo);
  }

  seleccionar(valor: '' | 'activa' | 'desactivada'): void {
    this.seleccion = valor;
    this.confirmoBloqueo = false; // cambiar la selección invalida la confirmación anterior
  }

  textoConfirmacion(activar: boolean): string {
    const nombre = this.asociacion?.nombre ? '«' + this.asociacion.nombre + '»' : 'la asociación';
    if (activar) {
      return 'Vas a activar ' + nombre + '.\n\nPodrá iniciar sesión con sus credenciales.\n\n¿Quieres continuar?';
    }
    const aviso = this.bloqueoActivo
      ? ''
      : '\n\nAviso: en este entorno el bloqueo de acceso por estado todavía no está activado, así que la asociación podrá seguir iniciando sesión hasta que se active. El cambio queda guardado y auditado.';
    return 'Vas a desactivar ' + nombre + '.\n\nUna asociación desactivada NO podrá iniciar sesión (ni con contraseña ni con QR) y su sesión actual perderá el acceso. No se borra ningún dato y podrás volver a activarla cuando quieras.' + aviso + '\n\n¿Seguro que quieres desactivarla?';
  }

  aplicar(): void {
    if (!this.puedeAplicar || !this.asociacion?.id && this.asociacion?.id !== 0) return;
    const activar = this.seleccion === 'activa';
    this.dialogo.openDialogAlert({
      title: activar ? 'Activar asociación' : 'Desactivar asociación',
      content: this.textoConfirmacion(activar),
      buttonsAlert: [AlertButtonType.Cancelar, AlertButtonType.Aceptar]
    }).afterClosed().subscribe(resultado => {
      if (resultado === AlertButtonType.Aceptar) this.guardar(activar);
    });
  }

  private guardar(activar: boolean): void {
    this.guardando = true;
    this.estadoService.cambiar(this.asociacion.id as number, activar).subscribe({
      next: active => {
        this.guardando = false;
        this.confirmoBloqueo = false;
        this.estadoCambiado.emit(active);
        this.alertas.success(activar ? 'La asociación ha quedado activa.' : 'La asociación ha quedado desactivada.', 4000);
      },
      error: (err: HttpErrorResponse) => {
        this.guardando = false;
        this.alertas.danger(err.error?.status?.message || err.error?.message || 'No se ha podido cambiar el estado de la asociación.', 5000);
      }
    });
  }
}
